import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CheckCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { cn, formatDate } from '@/lib/utils';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { pick, type Lang, type PostingApp, type PostingCatalog } from './model';

interface LogTabProps {
  app: PostingApp;
  catalog: PostingCatalog;
  lang: Lang;
  canEdit: boolean;
}

/** Posting exceptions — every auto-posting that did NOT produce a journal entry. */
export function LogTab({ app, catalog, lang, canEdit }: LogTabProps) {
  const { t } = useTranslation('postingRules');
  const [status, setStatus] = useState<'ALL' | 'FAILED' | 'SKIPPED'>('FAILED');
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.postingRules.log.list.useQuery({
    app,
    status: status === 'ALL' ? undefined : status,
    unresolvedOnly,
    page,
    limit: 50,
  });

  const invalidate = () => {
    utils.postingRules.log.list.invalidate();
    utils.postingRules.overview.invalidate({ app });
  };
  const resolveOne = trpc.postingRules.log.resolve.useMutation({
    onSuccess: invalidate,
    onError: (e) => showErrorToast(e.message),
  });
  const resolveAll = trpc.postingRules.log.resolveAll.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('log.resolvedCount', { count: r.count }));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const eventLabel = (event: string) => {
    const def = catalog.events.find((e) => e.event === event);
    return def ? pick(def.label, lang) : event;
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={(v) => { setStatus(v as typeof status); setPage(1); }}>
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FAILED">{t('log.status.FAILED')}</SelectItem>
            <SelectItem value="SKIPPED">{t('log.status.SKIPPED')}</SelectItem>
            <SelectItem value="ALL">{t('log.status.ALL')}</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={unresolvedOnly} onChange={(e) => { setUnresolvedOnly(e.target.checked); setPage(1); }} />
          {t('log.unresolvedOnly')}
        </label>
        <div className="flex-1" />
        {canEdit && (
          <Button
            variant="outline"
            className="h-8"
            disabled={resolveAll.isPending || !data || data.total === 0}
            onClick={() => resolveAll.mutate({ app, status: status === 'ALL' ? undefined : status })}
          >
            {resolveAll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCheck className="mr-2 h-4 w-4" />}
            {t('log.resolveAll')}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">{t('log.when')}</th>
              <th className="px-3 py-2 font-medium">{t('log.statusCol')}</th>
              <th className="px-3 py-2 font-medium">{t('log.event')}</th>
              <th className="px-3 py-2 font-medium">{t('log.reference')}</th>
              <th className="px-3 py-2 font-medium">{t('log.message')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">{t('log.empty')}</td>
              </tr>
            )}
            {data?.rows.map((row) => (
              <tr key={row.id} className={cn('border-b border-border last:border-b-0 align-top', row.resolvedAt && 'text-muted-foreground')}>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDate(row.createdAt, { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                      row.status === 'FAILED' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {t(`log.status.${row.status}`)}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {eventLabel(row.event)}
                  <div className="font-mono text-[11px] text-muted-foreground">{row.event}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                  {row.referenceType}
                  <div className="text-muted-foreground">{row.referenceId}</div>
                </td>
                <td className="px-3 py-2 text-xs">{row.message}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {canEdit && !row.resolvedAt && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={resolveOne.isPending} onClick={() => resolveOne.mutate({ app, id: row.id })}>
                      <Check className="mr-1 h-3.5 w-3.5" />
                      {t('log.resolve')}
                    </Button>
                  )}
                  {row.resolvedAt && <span className="text-[11px]">{t('log.resolvedAt', { date: formatDate(row.resolvedAt, { dateStyle: 'short' }) })}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.limit && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="sm" className="h-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹
          </Button>
          <span className="text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" className="h-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
